import { IgnitionGame } from './IgnitionGame.js';
import { Grid } from '../../core/Grid.js';
import { Direction, Position, parsePositionKey, oppositeDir } from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, canvasFont } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

const DIR_OFFSET: Record<Direction, { dr: number; dc: number }> = {
    N: { dr: -1, dc: 0 }, S: { dr: 1, dc: 0 }, E: { dr: 0, dc: 1 }, W: { dr: 0, dc: -1 }
};

// A fuse end anchored at a cell centre, with the direction its fuse leaves in.
interface FuseEndMark { pos: Position; dir: Direction; }

// How far past the rim a rim fuse's line + square extends.
const RIM_OVERSHOOT = 9;

export interface IgnitionRenderConfig extends Partial<RenderConfig> {
    defaultWallColor?: string;
    defaultWallWidth?: number;
    fuseColor?: string;
    fuseWidth?: number;
    fireColor?: string;
    indicatorRadius?: number;
    violationColor?: string;
}

const IGNITION_DEFAULTS = {
    defaultWallColor: COLORS.gray,
    defaultWallWidth: 6,
    fuseColor: COLORS.teal,
    fuseWidth: 4,
    fireColor: COLORS.amber,
    indicatorRadius: 10,
    violationColor: COLORS.red
};

// ── Fuse animation (docs/juice.md) ────────────────────────────────────
//
// A fuse runs cell centre to cell centre, so it is drawn on the way Line's and
// Flood's pieces are: out of the end that already carries fuse, since that is
// how a fuse network is extended. With both ends occupied (closing a gap) or
// neither (a stub out on its own) it opens from its midpoint instead, and a
// keyboard trace overrides all of that — see docs/juice.md.
//
// A rim half-segment is the exception: it pokes out of its cell to the grid
// edge, so it is always drawn that way round, gesture or not.
//
// The fire is *not* animated. It is simulation: ignition times, badges, the end
// marks and the selected digit's path all settle instantly.

type FuseOrigin = 'a' | 'b' | 'mid';

interface FuseTween {
    kind: 'enter' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    origin: FuseOrigin;
}

/** How much of a fuse is on the board this frame, as a span of its A→B line. */
interface FuseSpan {
    t0: number;
    t1: number;
    /** The whole thing — the resting state, and the only one that groups by cell. */
    full: boolean;
}

const FUSE_AT_REST: FuseSpan = { t0: 0, t1: 1, full: true };

interface Pixel { x: number; y: number; }

/** One end of a fuse inside the grid, with the direction the fuse leaves it in. */
interface FuseHalf { cell: Position; dir: Direction; }

export class IgnitionRenderer extends GridRenderer {
    private ignitionConfig: typeof IGNITION_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private fuseTweens = new Map<string, FuseTween>();
    private fuseState = new Set<string>();            // normalized keys, last frame
    private fuseFrame = new Map<string, FuseSpan>();  // where each sits this frame
    /** Which halves lay under a gray wall on their last real frame, so one on its
     *  way out retracts with its underlay instead of dropping it. */
    private fuseWallHalves = new Map<string, boolean[]>();
    private fusesSynced: boolean = false;             // first frame seeds, never animates
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: IgnitionGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: IgnitionRenderConfig = {}) {
        super(canvas, grid, config);
        this.ignitionConfig = { ...IGNITION_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every fuse at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (!enabled) this.fuseTweens.clear();
    }

    render(game: IgnitionGame): void {
        const grid = game.getGrid();

        this.lastGame = game;
        this.animating = false;
        this.syncFuseTweens(this.fuseKeys(game));
        this.fuseFrame = this.fuseFrames();

        this.clear();
        this.drawGrid();

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawIgnitionTimes(game);
        this.drawIgnitionPath(game);
        this.drawDefaultWalls();
        this.drawFuseWalls(game);
        this.drawFuseSegments();
        this.drawFuseDecorations(game);
        this.highlightViolations(game);
        if (isVortexGame(game)) { this.drawActiveVortexGates(game); }
        this.drawDigits(grid);
        this.drawIndicators(game);
        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    /** Scoring overlay: redraw the fuse segments (primary) in amber. */
    protected drawScoringPrimary(_game: IgnitionGame): void {
        this.drawFuseSegments(COLORS.amber);
    }

    // ── Fuse animation (docs/juice.md) ────────────────────────────────

    /** One normalized key per drawn segment — the two half-segments of an edge
     *  are the same piece. */
    private fuseKeys(game: IgnitionGame): Set<string> {
        const keys = new Set<string>();
        for (const segKey of game.getFuseSegments()) {
            const [rowStr, colStr, dir] = segKey.split(',');
            keys.add(this.normalizeSegmentKey(parseInt(rowStr), parseInt(colStr), dir as Direction));
        }
        return keys;
    }

    /** The two cells a key spans, in key order (A above or left of B). Either may
     *  sit off the grid, which is what makes it a rim segment. */
    private segCells(key: string): [Position, Position] {
        const [r1, c1, r2, c2] = key.split(',').map(Number);
        return [{ row: r1, col: c1 }, { row: r2, col: c2 }];
    }

    private isOnGrid(p: Position): boolean {
        return p.row >= 0 && p.row < this.grid.size && p.col >= 0 && p.col < this.grid.size;
    }

    /** Pixel ends of a segment: cell centres, or the point just past the rim a rim
     *  fuse runs out to. */
    private segPoints(key: string): [Pixel, Pixel] {
        const [a, b] = this.segCells(key);
        return [this.segEnd(a, b), this.segEnd(b, a)];
    }

    private segEnd(p: Position, other: Position): Pixel {
        if (this.isOnGrid(p)) return this.getCellCenter(p);
        const { padding, cellSize } = this.config;
        const c = this.getCellCenter(other);
        const far = padding + this.grid.size * cellSize;
        if (p.row < 0) return { x: c.x, y: padding - RIM_OVERSHOOT };
        if (p.row >= this.grid.size) return { x: c.x, y: far + RIM_OVERSHOOT };
        if (p.col < 0) return { x: padding - RIM_OVERSHOOT, y: c.y };
        return { x: far + RIM_OVERSHOOT, y: c.y };
    }

    /** The half-segments a key covers: one per on-grid end. A rim segment has one. */
    private segHalves(key: string): FuseHalf[] {
        const [a, b] = this.segCells(key);
        const vertical = a.row !== b.row;
        const halves: FuseHalf[] = [];
        if (this.isOnGrid(a)) halves.push({ cell: a, dir: vertical ? 'S' : 'E' });
        if (this.isOnGrid(b)) halves.push({ cell: b, dir: vertical ? 'N' : 'W' });
        return halves;
    }

    /**
     * Diff the fuses against the last frame and start a tween per change. A bulk
     * change (Reset, Restore best) fans out from the centre: fire leaves the middle
     * and works outward through the concentric chambers, so the network appears to
     * be laid along the way it will burn.
     */
    private syncFuseTweens(keys: Set<string>): void {
        if (!this.tweens.isEnabled()) {
            this.fuseTweens.clear();
            this.fuseState = keys;
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a saved
        // solution would redraw the whole network.
        if (!this.fusesSynced) {
            this.fuseState = keys;
            this.fusesSynced = true;
            return;
        }

        const added: string[] = [];
        const removed: string[] = [];
        for (const key of keys) if (!this.fuseState.has(key)) added.push(key);
        for (const key of this.fuseState) if (!keys.has(key)) removed.push(key);
        if (added.length === 0 && removed.length === 0) { this.fuseState = keys; return; }

        const mid = (this.grid.size - 1) / 2;
        const ring = (key: string) => {
            const [a, b] = this.segCells(key);
            return Math.max(
                Math.abs((a.row + b.row) / 2 - mid),
                Math.abs((a.col + b.col) / 2 - mid)
            );
        };
        const changed = [...added, ...removed].sort((x, y) => ring(x) - ring(y));

        const now = this.tweens.now();
        changed.forEach((key, i) => {
            const start = now + staggerDelay(i, changed.length);
            if (keys.has(key)) {
                // Grow out of the network as it stood *before* this piece joined it.
                this.fuseTweens.set(key, {
                    kind: 'enter', start, origin: this.fuseOrigin(key, this.fuseState, true)
                });
            } else {
                // Retract toward whatever is left of it now this piece has gone.
                this.fuseTweens.set(key, {
                    kind: 'exit', start, origin: this.fuseOrigin(key, keys, false)
                });
            }
        });

        this.fuseState = keys;
    }

    /**
     * Which end a fuse grows from.
     *
     * A rim stub answers for itself: it runs from its cell out to the grid edge, and
     * a line appearing outside the board before it is attached to anything would be
     * nonsense — so it is always drawn outward.
     *
     * Otherwise a keyboard trace decides, since it lays a fuse per hop and lands the
     * cursor on the cell it arrived at: the fuse grows *toward* that cell and
     * retracts into it. That has to be asked before connectivity, which gets it
     * backwards whenever you trace toward fuse that is already there — exactly when
     * you are joining a run up.
     *
     * Off a gesture it falls back to the board *without* this piece: the end that
     * already carries fuse, or the midpoint when both ends do (closing a gap) or
     * neither does (a piece out on its own).
     */
    private fuseOrigin(key: string, others: Set<string>, entering: boolean): FuseOrigin {
        const [a, b] = this.segCells(key);
        if (!this.isOnGrid(a)) return 'b';
        if (!this.isOnGrid(b)) return 'a';

        const cursor = this.keyboardFineCell();
        if (cursor) {
            const atA = cursor.row === a.row && cursor.col === a.col;
            const atB = cursor.row === b.row && cursor.col === b.col;
            if (atA !== atB) {
                if (entering) return atA ? 'b' : 'a';
                return atA ? 'a' : 'b';
            }
        }

        let onA = false, onB = false;
        for (const other of others) {
            if (other === key) continue;
            for (const p of this.segCells(other)) {
                if (p.row === a.row && p.col === a.col) onA = true;
                if (p.row === b.row && p.col === b.col) onB = true;
            }
            if (onA && onB) break;
        }
        if (onA === onB) return 'mid';
        return onA ? 'a' : 'b';
    }

    /**
     * Where every fuse sits this frame. This is the one place tweens are advanced
     * and retired, so the drawing passes — teal fuse, gray underlay, scoring overlay
     * — all read the same frame without any of them moving it on.
     */
    private fuseFrames(): Map<string, FuseSpan> {
        const frames = new Map<string, FuseSpan>();
        const now = this.tweens.now();

        for (const key of this.fuseState) {
            const tween = this.fuseTweens.get(key);
            if (!tween) { frames.set(key, FUSE_AT_REST); continue; }
            const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
            if (done) {
                this.fuseTweens.delete(key);
                frames.set(key, FUSE_AT_REST);
                continue;
            }
            this.animating = true;
            // The `exit` curve runs the *disappearance*, so invert it.
            frames.set(key, this.fuseSpan(tween.origin, tween.kind === 'enter' ? value : 1 - value));
        }

        // Fuses being removed are already gone from game state — they live on here
        // for the length of the exit and nowhere else.
        for (const [key, tween] of this.fuseTweens) {
            if (this.fuseState.has(key)) continue;
            const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
            if (done || tween.kind !== 'exit') { this.fuseTweens.delete(key); continue; }
            this.animating = true;
            frames.set(key, this.fuseSpan(tween.origin, 1 - value));
        }

        for (const key of this.fuseWallHalves.keys()) {
            if (!frames.has(key)) this.fuseWallHalves.delete(key);
        }
        return frames;
    }

    /** The `grown` fraction of a fuse, laid along its A→B line from `origin`. */
    private fuseSpan(origin: FuseOrigin, grown: number): FuseSpan {
        const g = Math.max(0, Math.min(1, grown));
        const [t0, t1] = origin === 'a' ? [0, g]
            : origin === 'b' ? [1 - g, 1]
            : [0.5 - g / 2, 0.5 + g / 2];
        return { t0, t1, full: g >= 1 };
    }

    private lerpPixel(a: Pixel, b: Pixel, t: number): Pixel {
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }

    /**
     * The visible piece of one half-segment this frame, in pixels, or null when the
     * fuse hasn't reached it. The half is a sub-line of the segment, so it is cut in
     * the segment's own parameter space rather than re-derived.
     */
    private halfPieceAt(key: string, half: FuseHalf, span: FuseSpan): [Pixel, Pixel] | null {
        const [pa, pb] = this.segPoints(key);
        const horizontal = Math.abs(pb.x - pa.x) > Math.abs(pb.y - pa.y);
        const param = (p: Pixel) => horizontal
            ? (p.x - pa.x) / (pb.x - pa.x)
            : (p.y - pa.y) / (pb.y - pa.y);

        let u0 = param(this.getCellCenter(half.cell));
        let u1 = param(this.edgePoint(half.cell, half.dir));
        if (u0 > u1) [u0, u1] = [u1, u0];

        const s0 = Math.max(u0, span.t0);
        const s1 = Math.min(u1, span.t1);
        if (s1 <= s0) return null;
        return [this.lerpPixel(pa, pb, s0), this.lerpPixel(pa, pb, s1)];
    }

    private drawIgnitionPath(game: IgnitionGame): void {
        const path = game.getIgnitionPath();
        if (!path || path.length === 0) return;

        const { padding, cellSize } = this.config;

        // Center grid point (corner shared by the 4 center cells)
        const centerGridPos = Math.floor(this.grid.size / 2);
        const centerX = padding + centerGridPos * cellSize;
        const centerY = padding + centerGridPos * cellSize;

        this.ctx.save();
        this.ctx.strokeStyle = COLORS.amber;
        this.ctx.lineWidth = 3;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.globalAlpha = 0.6;

        this.ctx.beginPath();
        this.ctx.moveTo(centerX, centerY);

        for (const pt of path) {
            if (pt.quarter === null) {
                const center = this.getCellCenter(pt);
                this.ctx.lineTo(center.x, center.y);
            } else {
                const cellLeft = padding + pt.col * cellSize;
                const cellTop = padding + pt.row * cellSize;
                const qx = cellLeft + (pt.quarter === 'NE' || pt.quarter === 'SE' ? 0.75 : 0.25) * cellSize;
                const qy = cellTop + (pt.quarter === 'SW' || pt.quarter === 'SE' ? 0.75 : 0.25) * cellSize;
                this.ctx.lineTo(qx, qy);
            }
        }
        this.ctx.stroke();

        // Amber dot at center
        this.ctx.globalAlpha = 1.0;
        this.ctx.fillStyle = COLORS.amber;
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, 4, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.restore();
    }

    private drawDefaultWalls(): void {
        const { padding, cellSize } = this.config;
        const { defaultWallColor, defaultWallWidth } = this.ignitionConfig;

        this.ctx.strokeStyle = defaultWallColor;
        this.ctx.lineWidth = defaultWallWidth;
        this.ctx.lineJoin = 'miter';
        this.ctx.lineCap = 'butt';

        // Draw concentric squares: ring -1 is the outer rim (the grid boundary
        // already blocks fire; this just encloses the outermost chamber too).
        const numRings = Math.floor((this.grid.size - 2) / 2);
        for (let ring = -1; ring < numRings; ring++) {
            const min = ring + 1;
            const max = this.grid.size - 1 - ring;

            // Calculate pixel positions
            const x1 = padding + min * cellSize;
            const y1 = padding + min * cellSize;
            const x2 = padding + max * cellSize;
            const y2 = padding + max * cellSize;

            this.ctx.beginPath();
            this.ctx.moveTo(x1, y1);
            this.ctx.lineTo(x2, y1);
            this.ctx.lineTo(x2, y2);
            this.ctx.lineTo(x1, y2);
            this.ctx.closePath();
            this.ctx.stroke();
        }
    }

    /**
     * Under the splitting portions of fuses, lay a thick gray segment matching the
     * default walls — the fuse line there separates two chambers, so it functions
     * as a wall. Drawn before the teal fuse so the fuse sits on top of it.
     */
    private drawFuseWalls(game: IgnitionGame): void {
        const { defaultWallColor, fuseWidth } = this.ignitionConfig;

        this.ctx.strokeStyle = defaultWallColor;
        // Noticeably wider than the fuse so the gray reads clearly on both sides.
        this.ctx.lineWidth = fuseWidth + 8;
        this.ctx.lineCap = 'butt';
        this.ctx.lineJoin = 'miter';

        // A settled fuse groups its splitting half-segments by cell, so a turning one
        // draws as a single path (edge → centre → edge) and the corner gets a clean
        // miter join. One still moving is cut to its visible span instead, half by
        // half — the underlay is part of the same line, so it arrives with it.
        const byCell = new Map<string, Direction[]>();
        const partials: Array<[Pixel, Pixel]> = [];

        for (const [key, span] of this.fuseFrame) {
            const halves = this.segHalves(key);
            const live = this.fuseState.has(key);
            // A fuse on its way out is gone from the game, so its underlay is read off
            // the last frame it existed rather than recomputed.
            const isWall = live
                ? halves.map(h => game.isFuseWall(h.cell.row, h.cell.col, h.dir))
                : this.fuseWallHalves.get(key) ?? halves.map(() => false);
            if (live) this.fuseWallHalves.set(key, isWall);

            halves.forEach((half, i) => {
                if (!isWall[i]) return;
                if (span.full) {
                    const cellKey = `${half.cell.row},${half.cell.col}`;
                    if (!byCell.has(cellKey)) byCell.set(cellKey, []);
                    byCell.get(cellKey)!.push(half.dir);
                    return;
                }
                const piece = this.halfPieceAt(key, half, span);
                if (piece) partials.push(piece);
            });
        }

        for (const [key, dirs] of byCell) {
            const [rowStr, colStr] = key.split(',');
            const cell = { row: parseInt(rowStr), col: parseInt(colStr) };
            const c = this.getCellCenter(cell);

            this.ctx.beginPath();
            if (dirs.length >= 2) {
                const p0 = this.edgePoint(cell, dirs[0]);
                const p1 = this.edgePoint(cell, dirs[1]);
                this.ctx.moveTo(p0.x, p0.y);
                this.ctx.lineTo(c.x, c.y);
                this.ctx.lineTo(p1.x, p1.y);
            } else {
                const p = this.edgePoint(cell, dirs[0]);
                this.ctx.moveTo(c.x, c.y);
                this.ctx.lineTo(p.x, p.y);
            }
            this.ctx.stroke();
        }

        for (const [p0, p1] of partials) {
            this.ctx.beginPath();
            this.ctx.moveTo(p0.x, p0.y);
            this.ctx.lineTo(p1.x, p1.y);
            this.ctx.stroke();
        }
    }

    /** Midpoint of one of a cell's four edges. */
    private edgePoint(cell: Position, dir: Direction): Pixel {
        const { padding, cellSize } = this.config;
        const c = this.getCellCenter(cell);
        switch (dir) {
            case 'N': return { x: c.x, y: padding + cell.row * cellSize };
            case 'S': return { x: c.x, y: padding + (cell.row + 1) * cellSize };
            case 'E': return { x: padding + (cell.col + 1) * cellSize, y: c.y };
            case 'W': return { x: padding + cell.col * cellSize, y: c.y };
        }
    }

    private drawFuseSegments(colorOverride?: string): void {
        const { fuseColor, fuseWidth } = this.ignitionConfig;

        this.ctx.strokeStyle = colorOverride ?? fuseColor;
        this.ctx.lineWidth = fuseWidth;
        this.ctx.lineCap = 'round';

        for (const [key, span] of this.fuseFrame) {
            // The scoring overlay draws the fuses that count; one on its way out
            // isn't one of them.
            if (colorOverride !== undefined && !this.fuseState.has(key)) continue;
            if (span.t1 <= span.t0) continue;

            const [pa, pb] = this.segPoints(key);
            const from = this.lerpPixel(pa, pb, span.t0);
            const to = this.lerpPixel(pa, pb, span.t1);
            this.ctx.beginPath();
            this.ctx.moveTo(from.x, from.y);
            this.ctx.lineTo(to.x, to.y);
            this.ctx.stroke();
        }
    }

    private isRimSegment(row: number, col: number, dir: Direction): boolean {
        return (dir === 'N' && row === 0) ||
            (dir === 'S' && row === this.grid.size - 1) ||
            (dir === 'E' && col === this.grid.size - 1) ||
            (dir === 'W' && col === 0);
    }

    /**
     * Decorate fuse ends and middles. Fire flows from lower to higher ignition
     * time, so each chamber tip is a source (partial circle) when its chamber's
     * time is ≤ the chain's far end, or a sink (sparkles) when higher. Pass-through
     * cells get a plain dot; ends running off the grid get a square past the rim.
     */
    private drawFuseDecorations(game: IgnitionGame): void {
        const { fuseColor } = this.ignitionConfig;

        // Group fuse half-segments by cell.
        const cellDirs = new Map<string, Direction[]>();
        for (const segKey of game.getFuseSegments()) {
            const [row, col, dir] = segKey.split(',');
            const key = `${row},${col}`;
            if (!cellDirs.has(key)) cellDirs.set(key, []);
            cellDirs.get(key)!.push(dir as Direction);
        }

        const circles: FuseEndMark[] = [];
        const sparks: FuseEndMark[] = [];
        const dots: Position[] = [];
        const tipSquares: Position[] = [];       // chamber tips of rim-bound fuses
        const rimSquares: FuseEndMark[] = [];

        for (const [key, dirs] of cellDirs) {
            const [rowStr, colStr] = key.split(',');
            const pos: Position = { row: parseInt(rowStr), col: parseInt(colStr) };

            const rim = dirs.filter(d => this.isRimSegment(pos.row, pos.col, d));
            const internal = dirs.filter(d => !this.isRimSegment(pos.row, pos.col, d));
            for (const d of rim) rimSquares.push({ pos, dir: d });

            if (dirs.length > 2) continue;                        // branching violation
            if (dirs.length === 2) { dots.push(pos); continue; }  // pass-through

            // Degree 1: a tip, leaving toward its single segment.
            const fuseDir = internal.length === 1 ? internal[0] : rim[0];
            const tSelf = game.getEndpointIgnitionTime(pos, fuseDir);
            if (tSelf === null) { dots.push(pos); continue; }     // unreached by fire

            // A lone rim stub runs straight to the rim; otherwise follow the chain.
            const far = internal.length === 1
                ? this.traceOtherEndTime(pos, internal[0], cellDirs, game)
                : 'rim';

            if (far === 'rim') tipSquares.push(pos);              // fuse to the rim: square both ends
            else if (far !== null && far < tSelf) sparks.push({ pos, dir: fuseDir });
            else circles.push({ pos, dir: fuseDir });
        }

        this.ctx.strokeStyle = fuseColor;
        this.ctx.fillStyle = fuseColor;
        for (const pos of dots) this.drawFuseDot(pos);
        for (const mark of circles) this.drawSourceRing(mark);
        for (const mark of sparks) this.drawSparkle(mark);
        for (const pos of tipSquares) this.drawSquare(this.getCellCenter(pos));
        for (const mark of rimSquares) this.drawRimSquare(mark);
    }

    /**
     * Walk a fuse's backbone from a chamber tip to its other end and return the
     * ignition time there, or null if it runs to the rim, branches, or loops.
     */
    private traceOtherEndTime(
        start: Position, firstDir: Direction, cellDirs: Map<string, Direction[]>, game: IgnitionGame
    ): number | 'rim' | null {
        let cur: Position = { row: start.row + DIR_OFFSET[firstDir].dr, col: start.col + DIR_OFFSET[firstDir].dc };
        let cameFrom = oppositeDir(firstDir);
        const visited = new Set<string>([`${start.row},${start.col}`]);

        while (true) {
            const key = `${cur.row},${cur.col}`;
            if (visited.has(key)) return null;  // loop
            visited.add(key);

            const dirs = cellDirs.get(key) ?? [];
            const onward = dirs.filter(d => d !== cameFrom && !this.isRimSegment(cur.row, cur.col, d));

            if (onward.length === 0) {
                // Backbone ends here: a chamber tip unless it also runs to the rim.
                if (dirs.some(d => this.isRimSegment(cur.row, cur.col, d))) return 'rim';
                return game.getEndpointIgnitionTime(cur, cameFrom);
            }
            if (onward.length > 1) return null;  // branching

            const nextDir = onward[0];
            cameFrom = oppositeDir(nextDir);
            cur = { row: cur.row + DIR_OFFSET[nextDir].dr, col: cur.col + DIR_OFFSET[nextDir].dc };
        }
    }

    private drawFuseDot(pos: Position): void {
        const c = this.getCellCenter(pos);
        this.ctx.beginPath();
        this.ctx.arc(c.x, c.y, 3.5, 0, Math.PI * 2);
        this.ctx.fill();
    }

    /** Partial circle around a source tip, open where the fuse line leaves. */
    private drawSourceRing({ pos, dir }: FuseEndMark): void {
        const c = this.getCellCenter(pos);
        const a = this.dirAngle(dir);
        const gap = 1.4;  // radians of opening, centred on the fuse direction
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.arc(c.x, c.y, 7, a + gap / 2, a - gap / 2 + Math.PI * 2);
        this.ctx.stroke();
    }

    /** Four short diagonal ticks around a sink tip, like a spark burst. */
    private drawSparkle({ pos }: FuseEndMark): void {
        const c = this.getCellCenter(pos);
        const r1 = 8, r2 = 13;
        this.ctx.lineWidth = 2;
        this.ctx.lineCap = 'round';
        for (const a of [Math.PI / 4, 3 * Math.PI / 4, 5 * Math.PI / 4, 7 * Math.PI / 4]) {
            const cos = Math.cos(a), sin = Math.sin(a);
            this.ctx.beginPath();
            this.ctx.moveTo(c.x + cos * r1, c.y + sin * r1);
            this.ctx.lineTo(c.x + cos * r2, c.y + sin * r2);
            this.ctx.stroke();
        }
    }

    /** Small square just past the rim, at the end of a rim fuse's extended line. */
    private drawRimSquare({ pos, dir }: FuseEndMark): void {
        const { padding, cellSize } = this.config;
        const c = this.getCellCenter(pos);
        let x = c.x, y = c.y;
        switch (dir) {
            case 'N': y = padding - RIM_OVERSHOOT; break;
            case 'S': y = padding + this.grid.size * cellSize + RIM_OVERSHOOT; break;
            case 'E': x = padding + this.grid.size * cellSize + RIM_OVERSHOOT; break;
            case 'W': x = padding - RIM_OVERSHOOT; break;
        }
        this.drawSquare({ x, y });
    }

    private drawSquare({ x, y }: { x: number; y: number }): void {
        const s = 7;
        this.ctx.fillRect(x - s / 2, y - s / 2, s, s);
    }

    private dirAngle(dir: Direction): number {
        switch (dir) {
            case 'E': return 0;
            case 'S': return Math.PI / 2;
            case 'W': return Math.PI;
            case 'N': return -Math.PI / 2;
        }
    }

    private normalizeSegmentKey(row: number, col: number, dir: Direction): string {
        // Normalize to avoid drawing the same segment twice
        let r1 = row, c1 = col, r2 = row, c2 = col;
        switch (dir) {
            case 'N': r2 = row - 1; break;
            case 'S': r2 = row + 1; break;
            case 'E': c2 = col + 1; break;
            case 'W': c2 = col - 1; break;
        }
        // Sort the cells
        if (r1 < r2 || (r1 === r2 && c1 < c2)) {
            return `${r1},${c1},${r2},${c2}`;
        }
        return `${r2},${c2},${r1},${c1}`;
    }

    private drawIgnitionTimes(game: IgnitionGame): void {
        const digitPositions = game.getDigitPositions();
        const digitPosKeys = new Set<string>();
        for (const [, pos] of digitPositions) {
            digitPosKeys.add(`${pos.row},${pos.col}`);
        }

        // Build set of cells that have fuses through them
        const cellsWithFuses = new Set<string>();
        for (const segKey of game.getFuseSegments()) {
            const [rowStr, colStr] = segKey.split(',');
            cellsWithFuses.add(`${rowStr},${colStr}`);
        }

        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                const key = `${row},${col}`;
                if (digitPosKeys.has(key)) continue;

                // Skip cells that have fuses through their center
                if (cellsWithFuses.has(key)) continue;

                // getIgnitionTime only returns a value if all 4 quarters agree
                const time = game.getIgnitionTime({ row, col });
                if (time === null) continue;

                const center = this.getCellCenter({ row, col });

                // Background circle for legibility above path
                this.ctx.fillStyle = COLORS.background;
                this.ctx.beginPath();
                this.ctx.arc(center.x, center.y, 8, 0, Math.PI * 2);
                this.ctx.fill();

                this.ctx.fillStyle = this.ignitionConfig.fireColor;
                this.ctx.font = canvasFont(12);
                this.ctx.textAlign = 'center';
                this.ctx.textBaseline = 'middle';
                this.ctx.fillText(time.toString(), center.x, center.y);
            }
        }
    }

    private highlightViolations(game: IgnitionGame): void {
        const violations = game.getBranchingViolations();
        const { violationColor } = this.ignitionConfig;

        for (const cellKey of violations) {
            const pos = parsePositionKey(cellKey);
            this.drawCellBorder(pos, violationColor);
        }
    }

    private drawIndicators(game: IgnitionGame): void {
        const correctDigits = new Set(game.getCorrectDigits());

        for (const [digit, pos] of game.getDigitPositions()) {
            const time = game.getIgnitionTime(pos) ?? (game as any).getAnyQuarterIgnitionTime?.(pos);
            if (time !== null && time !== undefined) {
                const isCorrect = correctDigits.has(digit);
                const badgeColor = isCorrect ? COLORS.teal : this.ignitionConfig.fireColor;
                this.drawBadge(pos, 'NE', time.toString(), badgeColor, 'sm', true);
            }
        }
    }

}
